<template>
  <v-form ref="form" v-model="formValid" lazy-validation>
    <v-container fluid>
      <v-layout row wrap>
        <v-flex xs6>
          <v-layout wrap>
            <v-flex xs12 pa-2>
              <v-text-field
                  type="number"
                  step="0.01"
                  :label="trans('app.interview.height') + ' (cm)'"
                  required
                  :rules="rules.height"
                  v-model="model.height"
              >
              </v-text-field>
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex xs6 pa-2>
              <v-text-field
                  type="number"
                  step="0.01"
                  :label="(trans('app.interview.initial_weight')) + ' (kg)'"
                  required
                  :rules="rules.initial_weight"
                  v-model="model.initial_weight"
              >
              </v-text-field>
            </v-flex>
            <v-flex xs6 pa-2>
              <v-text-field
                  type="number"
                  step="0.01"
                  :label="trans('app.interview.target_weight') + ' (kg)'"
                  required
                  :rules="rules.target_weight"
                  v-model="model.target_weight"
              >
              </v-text-field>
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex xs12 pa-2>
              <v-select v-model="model.activity"
                        :rules="rules.activity"
                        item-text="label"
                        item-value="value"
                        required
                        :items="activities"
                        :label="`app.interview.activity` | trans"
              ></v-select>
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex xs12 pa-2>
              <v-select v-model="model.psychological_condition"
                        v-if="patient.sex === 'female' && patient.age > 9"
                        item-text="label"
                        item-value="value"
                        :clearable="true"
                        :items="psychologicalConditions"
                        :label="`app.interview.psychological_condition` | trans"
              ></v-select>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs6>
          <v-layout wrap>
            <v-flex xs6 pa-2 class="layout justify-center align-center">
              <v-text-field
                  readonly
                  :label="'app.interview.bmi'|trans"
                  v-model="bmi.value"
              >
              </v-text-field>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon small v-on="on">help</v-icon>
                </template>
                <span>
              <span v-for="category in bmi.categories">
                <strong v-if="category.from === 0">
                  < {{ category.to }}
                </strong>
                <strong v-else-if="category.to === Infinity">
                  ≥ {{ category.from }}
                </strong>
                <strong v-else>
                  {{ category.from }} - {{ category.to }}
                </strong>
                - {{ `app.interview.bmi_level.` + category.name | trans }}
              </span>
            </span>
              </v-tooltip>
            </v-flex>
            <v-flex xs6 pa-2 class="layout justify-center align-center">
              <v-text-field
                  readonly
                  :label="(trans('app.interview.fair_weight')) + ' (kg)'"
                  v-model="fair_weight"
              >
              </v-text-field>
              <v-tooltip
                  top
              >
                <template v-slot:activator="{ on }">
                  <v-icon small v-on="on">help</v-icon>
                </template>
                <span v-html="trans('app.interview.fair_weight_formula')"></span>
              </v-tooltip>
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex xs12 pa-2>
              <v-layout wrap>
                <v-flex xs5 offset-xs2>
                  <span class="text-bold">{{ `app.harris-benedict` | trans }}</span>
                </v-flex>
                <v-flex xs5>
                  <span class="text-bold">{{ `app.mifflin` |trans }}</span>
                </v-flex>
              </v-layout>
              <v-layout wrap>
                <v-flex xs2>
                  <span>{{ `app.interview.ppm` | trans }}</span>
                  <v-tooltip
                      top
                  >
                    <template v-slot:activator="{ on }">
                      <v-icon small v-on="on">help</v-icon>
                    </template>
                    <span>{{ `app.interview.ppm_description` | trans }}</span>
                  </v-tooltip>
                </v-flex>
                <v-flex xs5>
                  <span>{{ bmrByHarrisBenedict }} kcal</span>
                </v-flex>
                <v-flex xs5>
                  <span>{{ bmrByMifflin }} kcal</span>
                </v-flex>
              </v-layout>
              <v-layout wrap>
                <v-flex xs2>
                  <span>{{ `app.interview.cpm` | trans }}</span>
                  <v-tooltip
                      top
                  >
                    <template v-slot:activator="{ on }">
                      <v-icon small v-on="on">help</v-icon>
                    </template>
                    <span>{{ `app.interview.cpm_description` | trans }}</span>
                  </v-tooltip>
                </v-flex>
                <v-flex xs5>
                  <span>{{ tmrByHarrisBenedict }} kcal</span>
                </v-flex>
                <v-flex xs5>
                  <span>{{ tmrByMifflin }} kcal</span>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex xs6 pa-2 class="layout justify-center align-center">
              <v-text-field
                  type="number"
                  step="0.01"
                  min="0"
                  :rules="rules.weight_change_per_week"
                  :label="'app.interview.weight_change_per_week' | trans"
                  v-model="model.weight_change_per_week"
              >
              </v-text-field>
              <v-tooltip
                  top
              >
                <template v-slot:activator="{ on }">
                  <v-icon small v-on="on">help</v-icon>
                </template>
                <span>{{ 'app.interview.weight_change_per_week_description'| trans }}</span>
              </v-tooltip>
            </v-flex>
            <v-flex xs6 pa-2 class="layout justify-center align-center">
              <v-text-field
                  readonly
                  :label="'app.interview.estimated_target_weight_date'|trans"
                  v-model="weightChangeTargetDate"
              >
              </v-text-field>
              <v-tooltip
                  top
              >
                <template v-slot:activator="{ on }">
                  <v-icon small v-on="on">help</v-icon>
                </template>
                <span>{{ 'app.interview.estimated_target_weight_date_description'|trans }}</span>
              </v-tooltip>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>
    <v-divider v-if="showSaveButton"></v-divider>
    <v-container fluid v-if="showSaveButton">
      <v-layout align-center justify-end>
        <v-btn @click="cancel()" :disabled="loading">{{ 'app.cancel' | trans }}</v-btn>
        <v-btn color="primary" @click="save()" :disabled="!formValid || loading" :loading="loading">
          {{ 'app.save' | trans }}
        </v-btn>
      </v-layout>
    </v-container>
  </v-form>
</template>
<script>
import {trans} from "../../../app";
import {helpers} from "../../../helpers";
import {mapGetters, mapActions} from "vuex";
import {bmiFactory} from "../../../factory/bmi.factory";

export default {
  props: {
    showSaveButton: {
      type: Boolean,
      required: false,
      default: false
    },
  },
  created() {
    this.loadModel();
  },
  data() {
    return {
      model: {
        initial_weight: null,
        target_weight: null,
        weight_change_per_week: null,
        height: null,
        activity: null,
        psychological_condition: null
      },
      formValid: false,
      rules: {
        initial_weight: [
          v => !!v || trans('validation.required', {'attribute': trans('app.interview.initial_weight')}),
          v => v && v >= 1 || trans('validation.min.numeric', {
            attribute: trans('app.interview.initial_weight'),
            min: 1,
          }),
        ],
        target_weight: [
          v => !!v || trans('validation.required', {'attribute': trans('app.interview.target_weight')}),
          v => v && v >= 1 || trans('validation.min.numeric', {
            attribute: trans('app.interview.target_weight'),
            min: 1,
          }),
        ],
        weight_change_per_week: [
          v => !!v || trans('validation.required', {'attribute': trans('app.interview.weight_change_per_week')}),
          v => v && v >= 0 || trans('validation.min.numeric', {
            attribute: trans('app.interview.weight_change_per_week'),
            min: 0,
          }),
        ],
        height: [
          v => !!v || trans('validation.required', {'attribute': trans('app.interview.height')}),
          v => v && v >= 100 || trans('validation.min.numeric', {
            attribute: trans('app.interview.height'),
            min: 100,
          }),
        ],
        activity: [
          v => !!v || trans('validation.required', {'attribute': trans('app.interview.activity')}),
        ],
      },
      activities: [
        {
          label: trans('app.interview.activities.1'),
          value: 1,
        },
        {
          label: trans('app.interview.activities.2'),
          value: 2,
        },
        {
          label: trans('app.interview.activities.3'),
          value: 3,
        },
        {
          label: trans('app.interview.activities.4'),
          value: 4,
        },
        {
          label: trans('app.interview.activities.5'),
          value: 5,
        },
        {
          label: trans('app.interview.activities.6'),
          value: 6,
        },
      ],
      psychologicalConditions: [
        {
          label: trans('app.interview.psychological_conditions.1'),
          value: 1,
        },
        {
          label: trans('app.interview.psychological_conditions.2'),
          value: 2,
        },
        {
          label: trans('app.interview.psychological_conditions.3'),
          value: 3,
        },
        {
          label: trans('app.interview.psychological_conditions.4'),
          value: 4,
        }
      ],
      loading: false,
    }
  },
  computed: {
    ...mapGetters(['patient', 'interview']),
    bmi: function () {
      if (this.patient && this.patient.sex && this.patient.age && this.model.initial_weight && this.model.height) {
        return bmiFactory.create(this.patient.sex, this.patient.age, this.model.initial_weight, this.model.height);
      }
      return {
        value: '-',
        categories: [],
      }
    },
    weightChangeTargetDate: function () {
      const weightChangeTargetDate = helpers.calculateTargetWeightDate(
          this.model.initial_weight,
          this.model.target_weight,
          this.model.weight_change_per_week
      );
      return weightChangeTargetDate === null ? '-' : weightChangeTargetDate;
    },
    fair_weight: function () {
      if (this.model.height) {
        const divider = this.patient.sex === 'female' ? 2 : 4;

        return (this.model.height - 100 - ((this.model.height - 150) / divider)).toFixed(2);
      }
      return '-';
    },
    bmrByHarrisBenedict: function () {
      const bmrByHarrisBenedict = helpers.calculateBmrByHarrisBenedict(
          this.patient.sex,
          this.model.initial_weight,
          this.model.height,
          this.patient.age
      );
      return bmrByHarrisBenedict === null ? '-' : bmrByHarrisBenedict;
    },
    bmrByMifflin: function () {
      const bmrByMifflin = helpers.calculateBmrByMifflin(
          this.patient.sex,
          this.model.initial_weight,
          this.model.height,
          this.patient.age
      )
      return bmrByMifflin === null ? '-' : bmrByMifflin;
    },
    tmrByHarrisBenedict: function () {
      const tmrByHarrisBenedict = helpers.calculateTmrByHarrisBenedict(
          this.patient.sex,
          this.model.initial_weight,
          this.model.height,
          this.patient.age,
          this.model.activity ? this.model.activity : null,
          this.model.psychological_condition ? this.model.psychological_condition : null,
      )
      return tmrByHarrisBenedict === null ? '-' : tmrByHarrisBenedict;
    },
    tmrByMifflin: function () {
      const tmrByMifflin = helpers.calculateTmrByMifflin(
          this.patient.sex,
          this.model.initial_weight,
          this.model.height,
          this.patient.age,
          this.model.activity ? this.model.activity : null,
          this.model.psychological_condition ? this.model.psychological_condition : null,
      );
      return tmrByMifflin === null ? '-' : tmrByMifflin;
    }
  },
  methods: {
    ...mapActions(['updateInterview', 'createInterview']),
    loadModel() {
      if (this.interview.id) {
        this.model.initial_weight = this.interview.initial_weight;
        this.model.target_weight = this.interview.target_weight;
        this.model.weight_change_per_week = this.interview.weight_change_per_week;
        this.model.height = this.interview.height;
        this.model.activity = this.interview.activity;
        this.model.psychological_condition = this.interview.psychological_condition;
      } else {
        this.model.weight_change_per_week = 0.5;
      }
    },
    cancel() {
      this.loadModel();
      this.$emit('canceled');
    },
    async save() {
      this.loading = true;
      if (this.$refs.form.validate() === false) {
        this.loading = false;
        this.$toast.error(trans('app.save_failed'));
        return false;
      }

      let updatedInterview = helpers.deepClone(this.interview);
      updatedInterview.patient_id = this.patient.id;
      updatedInterview.initial_weight = parseFloat(this.model.initial_weight);
      updatedInterview.target_weight = parseFloat(this.model.target_weight);
      updatedInterview.weight_change_per_week = parseFloat(this.model.weight_change_per_week);
      updatedInterview.height = parseFloat(this.model.height);
      updatedInterview.activity = this.model.activity;
      if (typeof this.model.psychological_condition === 'undefined') {
        this.model.psychological_condition = null;
      }
      updatedInterview.psychological_condition = this.model.psychological_condition;

      try {
        if (this.interview.id) {
          await this.updateInterview(updatedInterview);
        } else {
          await this.createInterview(updatedInterview);
        }
      } catch (error) {
        this.loading = false;
        this.sentryCaptureException(error);
        this.$toast.error(trans('app.save_failed'));
        return false;
      }

      this.loading = false;
      this.$toast.success(trans('app.actions.saved', {name: trans('app.patients.panel.interview').toLowerCase()}));
      this.$emit('saved');
      return true;
    }
  },
}
</script>
